/**
 * The node catalogue: everything the builder knows about each node kind that
 * is not presentation. Which group it sits in, what it is tinted, what it
 * starts with, which output ports it has, how it summarises itself on the
 * canvas, and what is wrong with it.
 *
 * Pure data and functions, no React, so `graph-ops` and the mapper tests can
 * use it under Node.
 */
import type { FlowNodeData, FlowNodeOutput } from '@repletoryreach/sdk-ui/dnd';
import { whatsAppPreview } from '@repletoryreach/sdk-utils/whatsapp-text';
import type { JsonObject } from '../mapper';
import { isPlainObject } from '../mapper';

export type NodeGroup = 'trigger' | 'actions' | 'logic';

/** A problem a node reports about its own fields; the graph adds `nodeId`. */
export interface NodeProblem {
  key: string;
  vars?: Record<string, string | number>;
  severity?: 'warning' | 'error';
}

export interface NodeKindSpec {
  kind: string;
  group: NodeGroup;
  /** Icon name, resolved by `nodes.tsx`. */
  icon: string;
  /** Accent colour for the card and the palette entry. */
  tint: string;
  defaults: JsonObject;
  /** Output ports. One entry (or none) means a single default way out. */
  outputs: (data: JsonObject) => FlowNodeOutput[];
  /** One line under the title on the canvas. Returns an i18n key + vars or plain text. */
  summary: (data: JsonObject) => { key: string; vars?: Record<string, string | number> } | { text: string } | null;
  validate?: (data: JsonObject) => NodeProblem[];
  /**
   * The channel rule this step falls under, as `ChannelRules.RuleFor` in
   * flows-llm names it (`delay`, `template`, `llm.reply`, …), or null when it
   * runs on every channel. `UNAVAILABLE_ON` says which channels refuse it.
   */
  channelRule?: (data: JsonObject) => ChannelRuleCode | null;
}

/**
 * Steps a channel cannot run (4 October 2026), mirroring
 * `services/flows-llm/Services/Engine/ChannelRules.cs` exactly: a USSD screen
 * waits a few seconds and shows only text, so a template, a picture, a delay,
 * a hand-over or an AI-written reply cannot run there. Sorting and pulling
 * details out with the AI still work. The builder warns; it never hides.
 */
export type ChannelRuleCode = 'template' | 'media' | 'delay' | 'handoff' | 'llm.reply' | 'llm.rewrite' | 'http.ai_reply';
export const UNAVAILABLE_ON: Record<string, readonly ChannelRuleCode[]> = {
  ussd: ['template', 'media', 'delay', 'handoff', 'llm.reply', 'llm.rewrite', 'http.ai_reply']
};

const str = (v: unknown, max = 60): string => {
  const s = typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

/**
 * A message body on a node card. What the author typed is WhatsApp markup, so
 * one clipped line of it would otherwise read "*Thanks!* We are _on it_" with
 * the markers showing. The card shows the words; the inspector shows the
 * markup, which is where it can be edited.
 */
const body = (v: unknown, max = 60): string => (typeof v === 'string' ? whatsAppPreview(v, max) : str(v, max));

const YES_NO: FlowNodeOutput[] = [{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }]; // i18n-ignore: port ids; labels are re-mapped by nodes.tsx
const SUCCESS_ERROR: FlowNodeOutput[] = [{ id: 'success', label: 'Success' }, { id: 'error', label: 'Error' }]; // i18n-ignore: port ids
const OPEN_CLOSED: FlowNodeOutput[] = [{ id: 'open', label: 'Open' }, { id: 'closed', label: 'Closed' }]; // i18n-ignore: port ids
const FOUND_MISSING: FlowNodeOutput[] = [{ id: 'found', label: 'Found' }, { id: 'missing', label: 'Missing' }]; // i18n-ignore: port ids
const ANSWERED_UNSURE: FlowNodeOutput[] = [{ id: 'answered', label: 'Answered' }, { id: 'unsure', label: 'Not sure' }]; // i18n-ignore: port ids
/** The Look up step's exits (19 October 2026), as `LookupNodeAsync` in flows-llm names them. */
export const LOOKUP_OUTPUTS: FlowNodeOutput[] = [{ id: 'found', label: 'Found' }, { id: 'not_found', label: 'Not found' }, { id: 'error', label: 'Error' }]; // i18n-ignore: port ids; labels re-mapped by nodes.tsx
/** The Book step's exits (7 October 2026), as `BookingSteps` in flows-llm names them. */
export const BOOKING_OUTPUTS: FlowNodeOutput[] = [{ id: 'booked', label: 'Booked' }, { id: 'requested', label: 'Requested' }, { id: 'full', label: 'Full' }, { id: 'cancelled', label: 'Cancelled' }]; // i18n-ignore: port ids; labels re-mapped by nodes.tsx
/** The Manage booking step's exits. */
export const MANAGE_BOOKING_OUTPUTS: FlowNodeOutput[] = [{ id: 'done', label: 'Done' }, { id: 'none', label: 'No bookings' }, { id: 'too_late', label: 'Too late' }]; // i18n-ignore: port ids; labels re-mapped by nodes.tsx

/** The Ticket step's exits (7 October 2026), as `TicketSteps` in flows-llm names them. */
export const TICKET_OUTPUTS: FlowNodeOutput[] = [{ id: 'created', label: 'Created' }, { id: 'failed', label: 'Failed' }]; // i18n-ignore: port ids; labels re-mapped by nodes.tsx
/** The Ticket status step's exits. */
export const TICKET_STATUS_OUTPUTS: FlowNodeOutput[] = [{ id: 'answered', label: 'Answered' }, { id: 'none', label: 'None' }]; // i18n-ignore: port ids; labels re-mapped by nodes.tsx
/** The subject the engine uses when the step names none. */
export const TICKET_DEFAULT_SUBJECT = '{{contact.name}}: {{last_message}}'; // i18n-ignore: a template, not copy

/**
 * The Change language step's exits (3 November 2026), as `LanguageStep` in flows-llm names them: `changed` after a
 * pick, `kept` when the customer chose none. The engine follows `changed` when `kept` has no link of its own.
 */
export const LANGUAGE_OUTPUTS: FlowNodeOutput[] = [{ id: 'changed', label: 'Changed' }, { id: 'kept', label: 'Kept' }]; // i18n-ignore: port ids; labels re-mapped by nodes.tsx

/**
 * Custom contact fields a hand-built language menu saves the pick to (3 November 2026). Africa Connect saved theirs
 * to `language` three times; the chatbot reads none of these, so the chat stayed in English. The same names
 * `DefinitionLint.LanguageFields` in flows-llm warns about, compared without regard to case.
 */
export const LANGUAGE_FIELD_NAMES: readonly string[] = ['language', 'lang', 'language_choice', 'preferred_language', 'chat_language'];

/** The first custom field an Update contact step saves that looks like a language choice, or null. */
export function languageField(d: JsonObject): string | null {
  if (!isPlainObject(d.fields)) return null;
  return Object.keys(d.fields).find((k) => LANGUAGE_FIELD_NAMES.includes(k.trim().toLowerCase())) ?? null;
}

/** The field values a Ticket step fills in, as field key and template, in the order written. */
export function ticketFields(d: JsonObject): Array<{ key: string; value: string }> {
  if (!isPlainObject(d.fields)) return [];
  return Object.entries(d.fields).map(([key, value]) => ({ key, value: typeof value === 'string' ? value : value == null ? '' : JSON.stringify(value) }));
}

/** A Ticket step's tags. */
export function ticketTags(d: JsonObject): string[] {
  return Array.isArray(d.tags) ? d.tags.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim()) : [];
}

/** What the engine's lint says about a Ticket step (DefinitionLint.TicketProblems), in the builder's words. */
function ticketProblems(d: JsonObject): NodeProblem[] {
  const problems: NodeProblem[] = [];
  for (const key of ['type_id', 'priority_id', 'team_id', 'subject', 'description']) {
    if (d[key] !== undefined && d[key] !== null && typeof d[key] !== 'string') problems.push({ key: 'flows.validate.ticketNotText', vars: { field: key }, severity: 'error' });
  }
  if (d.fields !== undefined && d.fields !== null) {
    if (!isPlainObject(d.fields) || Object.entries(d.fields).some(([k, v]) => !k.trim() || typeof v !== 'string')) problems.push({ key: 'flows.validate.ticketFields', severity: 'error' });
  }
  if (d.tags !== undefined && d.tags !== null && !(Array.isArray(d.tags) && d.tags.every((x) => typeof x === 'string' && x.trim()))) problems.push({ key: 'flows.validate.ticketTags', severity: 'error' });
  if (d.notify_client !== undefined && d.notify_client !== null && typeof d.notify_client !== 'boolean') problems.push({ key: 'flows.validate.ticketNotify', severity: 'error' });
  return problems;
}

/** The days a booking step looks ahead, as the engine reads `days_ahead`: 14 when unset, at most 90. */
export const BOOKING_DAYS_DEFAULT = 14;
export const BOOKING_DAYS_MAX = 90;

/** Who the customer books with: anyone free, their own choice, or one person (a staff id). */
export function bookingStaff(d: JsonObject): string {
  return typeof d.staff === 'string' && d.staff.trim() ? d.staff.trim() : 'any';
}

/** Whether the customer picks the service: said so, or no service chosen. */
export function bookingChoosesService(d: JsonObject): boolean {
  return d.choose_service === true || !(typeof d.service_id === 'string' && d.service_id.trim());
}

function bookingDaysProblem(d: JsonObject): NodeProblem[] {
  if (d.days_ahead === undefined || d.days_ahead === null || d.days_ahead === '') return [];
  const n = Number(d.days_ahead);
  return Number.isInteger(n) && n >= 1 && n <= BOOKING_DAYS_MAX ? [] : [{ key: 'flows.validate.bookingDays', vars: { max: BOOKING_DAYS_MAX }, severity: 'error' }];
}

/** Labels (classify) or fields (extract) an AI node lists, in either shape the engine reads. */
export function namedItems(data: JsonObject, key: string): Array<{ id: string; title: string }> {
  const raw = Array.isArray(data[key]) ? (data[key] as unknown[]) : [];
  const out: Array<{ id: string; title: string }> = [];
  raw.forEach((o, i) => {
    if (o && typeof o === 'object') {
      const obj = o as JsonObject;
      const id = String(obj.id ?? obj.name ?? '').trim();
      const title = String(obj.title ?? obj.description ?? obj.label ?? '').trim();
      if (!id && !title) return;
      out.push({ id: id || slug(title, i + 1), title: title || id });
    } else if (typeof o === 'string' && o.trim()) {
      out.push({ id: slug(o, i + 1), title: o.trim() });
    }
  });
  return out;
}

/** The cases of a switch: value (or comma-separated values) per branch. */
export function switchCases(data: JsonObject): Array<{ id: string; value: string }> {
  const raw = Array.isArray(data.cases) ? (data.cases as unknown[]) : [];
  const out: Array<{ id: string; value: string }> = [];
  raw.forEach((c, i) => {
    if (c && typeof c === 'object') {
      const obj = c as JsonObject;
      const value = String(obj.value ?? obj.title ?? '').trim();
      if (!value) return;
      out.push({ id: String(obj.id ?? '').trim() || slug(value, i + 1), value });
    } else if (typeof c === 'string' && c.trim()) {
      out.push({ id: slug(c, i + 1), value: c.trim() });
    }
  });
  return out;
}

/**
 * The operators a Decision rule offers, grouped as the dropdown lists them.
 * The same names the engine's `Conditions.KnownOps` holds (a unit test there
 * checks every one of these is known).
 */
export const DECISION_OPS = {
  presence: ['filled', 'not_filled', 'is_true', 'is_false'],
  text: ['eq', 'neq', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in', 'not_in', 'matches'],
  number: ['gt', 'gte', 'lt', 'lte'],
  date: ['is_today', 'on_date', 'before', 'after', 'on_or_before', 'on_or_after', 'within_days', 'within_last_days', 'older_than_days']
} as const;
export const DECISION_OP_LIST: string[] = Object.values(DECISION_OPS).flat();
export type OpRight = 'none' | 'text' | 'number' | 'date' | 'days';
/** What a rule's right-hand side is, by operator: nothing, text, a number, a date or a count of days. */
export function opRight(op: string): OpRight {
  if (['filled', 'not_filled', 'is_true', 'is_false', 'is_today', 'exists', 'not_exists', 'empty', 'not_empty'].includes(op)) return 'none';
  if (['within_days', 'within_last_days', 'older_than_days'].includes(op)) return 'days';
  if (['on_date', 'before', 'after', 'on_or_before', 'on_or_after'].includes(op)) return 'date';
  if (['gt', 'gte', 'lt', 'lte'].includes(op)) return 'number';
  return 'text';
}

export interface DecisionRule { left: string; op: string; right: string }
export interface DecisionPath { id: string; label: string; match: 'all' | 'any'; rules: DecisionRule[] }
export const DECISION_OTHERWISE = 'otherwise';

/**
 * The paths of a Decision step, with the exit id each one takes. A path keeps
 * the id it was given; one without is named from its label, and a name that
 * is `otherwise` or already taken gets a number, exactly as the engine does
 * (`FlowEngine.DecisionNode`), so both draw the same exits.
 */
export function decisionPaths(data: JsonObject): DecisionPath[] {
  const raw = Array.isArray(data.paths) ? (data.paths as unknown[]) : [];
  const taken = new Set<string>();
  const out: DecisionPath[] = [];
  raw.forEach((p, i) => {
    if (!p || typeof p !== 'object') return;
    const obj = p as JsonObject;
    const label = String(obj.label ?? '').trim();
    const base = String(obj.id ?? '').trim() || slug(label, i + 1);
    let id = base;
    for (let n = 2; id.toLowerCase() === DECISION_OTHERWISE || taken.has(id.toLowerCase()); n++) id = `${base}_${n}`;
    taken.add(id.toLowerCase());
    const rules = (Array.isArray(obj.rules) ? obj.rules : []).filter(isPlainObject).map((r) => ({
      left: String(r.left ?? r.variable ?? '').trim(),
      op: String(r.op ?? r.operator ?? 'eq').trim() || 'eq',
      right: r.right == null ? String(r.value ?? '') : String(r.right)
    }));
    out.push({ id, label, match: obj.match === 'any' ? 'any' : 'all', rules });
  });
  return out;
}

/** The branches of a split test, with their weights. */
export function splitBranches(data: JsonObject): Array<{ id: string; title: string; weight: number }> {
  const raw = Array.isArray(data.branches) ? (data.branches as unknown[]) : [];
  const out = raw.flatMap((b, i) => {
    if (!b || typeof b !== 'object') return [];
    const obj = b as JsonObject;
    const title = String(obj.title ?? obj.id ?? `Branch ${i + 1}`).trim(); // i18n-ignore: a default id, replaced by the inspector
    const id = String(obj.id ?? '').trim() || slug(title, i + 1);
    const weight = Number(obj.weight);
    return [{ id, title, weight: Number.isFinite(weight) && weight >= 0 ? weight : 1 }];
  });
  return out.length ? out : [{ id: 'a', title: 'A', weight: 50 }, { id: 'b', title: 'B', weight: 50 }];
}

export type KbScope = 'all' | 'selected' | 'none';
/** Where an AI answer step reads from: every document, the ticked ones, or none. Older nodes without the field: the ticked ones when any, else none. */
export function kbScope(data: JsonObject): KbScope {
  if (data.kb_scope === 'all' || data.kb_scope === 'selected' || data.kb_scope === 'none') return data.kb_scope;
  return Array.isArray(data.kb_ids) && data.kb_ids.length > 0 ? 'selected' : 'none';
}

/**
 * Where an Hours step takes its hours from (7 October 2026): the business's
 * own opening hours (Settings, Organisation) or hours set on the step. A step
 * saved before the choice existed has days or times of its own and stays
 * custom; one with none follows the business. The engine reads it the same way.
 */
export type HoursSource = 'business' | 'custom';
export function hoursSource(data: JsonObject): HoursSource {
  if (data.source === 'business' || data.source === 'custom') return data.source;
  const own = (Array.isArray(data.days) && data.days.length > 0)
    || (typeof data.open === 'string' && data.open.trim() !== '')
    || (typeof data.close === 'string' && data.close.trim() !== '');
  return own ? 'custom' : 'business';
}

export type NotifyChannelType = 'email' | 'sms' | 'whatsapp' | 'webhook';
export const NOTIFY_CHANNEL_TYPES: NotifyChannelType[] = ['email', 'sms', 'whatsapp', 'webhook'];
/** The ways a notify step reaches the team. `target` is the address, number or URL. */
export function notifyChannels(data: JsonObject): Array<{ type: NotifyChannelType; target: string }> {
  const raw = Array.isArray(data.channels) ? (data.channels as unknown[]) : [];
  return raw.flatMap((c) => {
    if (!c || typeof c !== 'object') return [];
    const obj = c as JsonObject;
    const t = String(obj.type ?? 'email').toLowerCase();
    const type: NotifyChannelType = t === 'sms' || t === 'whatsapp' || t === 'webhook' ? t : t === 'api' || t === 'http' ? 'webhook' : 'email';
    return [{ type, target: String(obj.to ?? obj.url ?? '').trim() }];
  });
}
export function validTarget(c: { type: NotifyChannelType; target: string }): boolean {
  if (c.type === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.target);
  if (c.type === 'webhook') return /^https?:\/\/\S+$/i.test(c.target);
  return /^\+?[0-9][0-9\s()-]{7,}$/.test(c.target);
}

/** Every rule an answer can be checked with; the engine tidies the value it stores (a phone as +27…, a date as yyyy-mm-dd). */
export const VALIDATE_KINDS = ['none', 'not_empty', 'name', 'email', 'phone', 'number', 'integer', 'date', 'time', 'datetime', 'yes_no', 'id_number', 'url', 'postcode', 'text', 'regex'] as const;
export type ValidateKind = (typeof VALIDATE_KINDS)[number];
/** What a form field's answer must be: the same rules, plus a choice from a list. */
export const FIELD_TYPES = ['text', 'name', 'email', 'phone', 'number', 'integer', 'date', 'time', 'datetime', 'yes_no', 'choice', 'id_number', 'url', 'postcode', 'not_empty'] as const;
export type FieldType = (typeof FIELD_TYPES)[number];
/** Countries a phone number or postal code can be read for (the dialling code is added when the customer leaves it out). */
export const COUNTRY_CODES = ['ZA', 'NA', 'BW', 'ZW', 'MZ', 'LS', 'SZ', 'ZM', 'MW', 'KE', 'NG', 'GH', 'TZ', 'UG', 'GB', 'US', 'AU', 'DE', 'IN', 'AE'] as const;
/** How an HTTP request proves who it is. */
export const HTTP_AUTH_TYPES = ['none', 'bearer', 'basic', 'api_key', 'custom_header', 'oauth2'] as const;
export type HttpAuthType = (typeof HTTP_AUTH_TYPES)[number];

export interface FormFieldSpec { name: string; question: string; type: FieldType; required: boolean; options: string[]; label?: string; always_ask?: boolean }

/** The fields of a form node, in either shape the engine reads. */
export function formFields(data: JsonObject): FormFieldSpec[] {
  if (!Array.isArray(data.fields)) return [];
  return data.fields.filter(isPlainObject).map((f, i) => {
    const rawType = typeof f.type === 'string' ? f.type : typeof f.validate === 'string' ? f.validate : 'text';
    const type = (FIELD_TYPES as readonly string[]).includes(rawType) ? (rawType as FieldType) : rawType === 'options' || rawType === 'select' || rawType === 'option' ? 'choice' : 'text';
    const options = Array.isArray(f.options)
      ? f.options.map((o) => (isPlainObject(o) ? String(o.title ?? o.id ?? '') : String(o ?? ''))).filter(Boolean)
      : [];
    const name = typeof f.name === 'string' && f.name.trim() ? f.name.trim() : typeof f.variable === 'string' && f.variable.trim() ? f.variable.trim() : `field_${i + 1}`;
    return {
      name,
      question: typeof f.question === 'string' ? f.question : '',
      type: options.length > 0 && type === 'text' ? 'choice' : type,
      required: f.required !== false,
      options,
      ...(typeof f.label === 'string' && f.label.trim() ? { label: f.label.trim() } : {}),
      // Kept through every edit (8 October 2026): a field that asks even when the answer is already known.
      ...(f.always_ask === true ? { always_ask: true } : {})
    };
  });
}

/** A sample of the object a form composes, for the preview under the fields. */
export function formSample(fields: FormFieldSpec[]): Record<string, unknown> {
  const sample: Record<string, unknown> = {};
  for (const f of fields) {
    sample[f.name] = f.type === 'number' ? 42 : f.type === 'integer' ? 3 : f.type === 'email' ? 'thandi@example.com' : f.type === 'phone' ? '+27821234567'
      : f.type === 'date' ? '2026-09-12' : f.type === 'time' ? '14:30' : f.type === 'yes_no' ? 'yes' : f.type === 'choice' ? (f.options[0] ? slug(f.options[0], 1) : 'a')
      : f.type === 'name' ? 'Thandi Nkosi' : f.type === 'id_number' ? '9001015009087' : f.type === 'url' ? 'https://example.com' : f.type === 'postcode' ? '2196' : '…';
  }
  return sample;
}

export type LlmMode = 'reply' | 'classify' | 'extract' | 'rewrite';
export function llmMode(data: JsonObject): LlmMode {
  return data.mode === 'classify' || data.mode === 'extract' || data.mode === 'rewrite' ? data.mode : 'reply';
}

/** Options an `ask` presents, in either of the shapes the engine reads. */
/** An Ask step that builds its menu from a list (`options_from`, 19 October 2026). */
export const listMenu = (d: JsonObject): boolean => typeof d.options_from === 'string' && d.options_from.trim().length > 0;
/** How many options of its own a list menu may add after the items (Cancel, Another date). */
export const LIST_MAX_OWN = 2;

export function askOptions(data: JsonObject): Array<{ id: string; title: string }> {
  const raw = Array.isArray(data.options) ? data.options : Array.isArray(data.buttons) ? data.buttons : [];
  const out: Array<{ id: string; title: string }> = [];
  raw.forEach((o, i) => {
    if (o && typeof o === 'object') {
      const obj = o as JsonObject;
      const title = String(obj.title ?? obj.label ?? '').trim();
      if (!title) return;
      const id = String(obj.id ?? obj.value ?? '').trim() || slug(title, i + 1);
      out.push({ id, title });
    } else if (typeof o === 'string' && o.trim()) {
      out.push({ id: slug(o, i + 1), title: o.trim() });
    }
  });
  return out;
}

/** The same slug the engine derives for an option without an id. */
export function slug(title: string, index: number): string {
  const s = title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return s ? s.slice(0, 40) : `opt${index}`;
}

export const NODE_KINDS: Record<string, NodeKindSpec> = {
  send: {
    kind: 'send',
    channelRule: (d) => (typeof d.media_url === 'string' && d.media_url.trim() ? 'media' : null),
    group: 'actions',
    icon: 'message',
    tint: '#0ea5e9',
    defaults: { name: '', body: '' },
    outputs: () => [],
    summary: (d) => (typeof d.body === 'string' && d.body.trim()
      ? { text: body(d.body) }
      : typeof d.media_url === 'string' && d.media_url
        ? { key: 'flows.builder.summary.media' }
        : { key: 'flows.builder.summary.lastAnswer' }),
    validate: (d) => {
      const problems: NodeProblem[] = [];
      const options = askOptions(d);
      if (options.length > 10) problems.push({ key: 'flows.validate.tooManyOptions', severity: 'error' });
      return problems;
    }
  },
  reply: {
    kind: 'reply',
    channelRule: (d) => (typeof d.media_url === 'string' && d.media_url.trim() ? 'media' : null),
    group: 'actions',
    icon: 'message',
    tint: '#0ea5e9',
    defaults: { body: '' },
    outputs: () => [],
    summary: (d) => (typeof d.body === 'string' && d.body.trim() ? { text: body(d.body) } : { key: 'flows.builder.summary.lastAnswer' })
  },
  ask: {
    kind: 'ask',
    group: 'actions',
    icon: 'question',
    tint: '#14b8a6',
    defaults: { name: '', question: '', variable: '', validate: 'none', max_retries: 2 },
    outputs: (d) => {
      const options = askOptions(d);
      // A menu from a list (19 October 2026): the chosen item, the author's own options, an empty list, no match.
      if (listMenu(d)) return [{ id: 'chosen', label: 'Chosen' }, ...options.slice(0, LIST_MAX_OWN).map((o) => ({ id: o.id, label: o.title })), { id: 'empty', label: 'Empty' }, { id: 'invalid', label: 'No match' }]; // i18n-ignore: port ids; labels re-mapped by nodes.tsx
      if (options.length === 0) return [];
      return [...options.map((o) => ({ id: o.id, label: o.title })), { id: 'invalid', label: 'No match' }]; // i18n-ignore: port id
    },
    summary: (d) => {
      if (listMenu(d)) return { key: 'flows.builder.summary.askList', vars: { list: str(d.options_from, 36) } };
      const q = typeof d.question === 'string' ? d.question : typeof d.body === 'string' ? d.body : '';
      const v = typeof d.variable === 'string' && d.variable ? ` → {{${d.variable}}}` : '';
      return q.trim() ? { text: `${body(q, 44)}${v}` } : { key: 'flows.builder.summary.noQuestion' };
    },
    validate: (d) => {
      const problems: NodeProblem[] = [];
      const q = typeof d.question === 'string' ? d.question : typeof d.body === 'string' ? d.body : '';
      if (!q.trim()) problems.push({ key: 'flows.validate.askNoQuestion', severity: 'error' });
      if (!(typeof d.variable === 'string' && d.variable.trim())) problems.push({ key: 'flows.validate.askNoVariable', severity: 'warning' });
      if (typeof d.variable === 'string' && d.variable && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(d.variable)) {
        problems.push({ key: 'flows.validate.badVariableName', vars: { name: d.variable }, severity: 'error' });
      }
      if (askOptions(d).length > 10) problems.push({ key: 'flows.validate.tooManyOptions', severity: 'error' });
      if (listMenu(d) && askOptions(d).length > LIST_MAX_OWN) problems.push({ key: 'flows.validate.listTooManyOwn', vars: { max: LIST_MAX_OWN }, severity: 'error' });
      return problems;
    }
  },
  form: {
    kind: 'form',
    group: 'actions',
    icon: 'clipboard',
    tint: '#0f766e',
    defaults: { name: '', intro: '', fields: [], confirm: true, save_as: 'form', done_message: '' },
    outputs: () => [{ id: 'submitted', label: 'Submitted' }, { id: 'cancelled', label: 'Cancelled' }], // i18n-ignore: port ids
    summary: (d) => {
      const fields = formFields(d);
      if (fields.length === 0) return { key: 'flows.builder.summary.formEmpty' };
      const saveAs = typeof d.save_as === 'string' && d.save_as.trim() ? d.save_as.trim() : 'form';
      return { key: 'flows.builder.summary.formFields', vars: { count: fields.length, var: saveAs, names: str(fields.map((f) => f.name).join(', '), 40) } };
    },
    validate: (d) => {
      const problems: NodeProblem[] = [];
      const fields = formFields(d);
      if (fields.length === 0) { problems.push({ key: 'flows.validate.formNoFields', severity: 'error' }); return problems; }
      const seen = new Set<string>();
      for (const f of fields) {
        if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(f.name)) problems.push({ key: 'flows.validate.formBadField', vars: { name: f.name }, severity: 'error' });
        if (seen.has(f.name)) problems.push({ key: 'flows.validate.formDuplicateField', vars: { name: f.name }, severity: 'error' });
        seen.add(f.name);
        if (!f.question.trim()) problems.push({ key: 'flows.validate.formNoQuestion', vars: { name: f.name }, severity: 'error' });
        if (f.type === 'choice' && f.options.length === 0) problems.push({ key: 'flows.validate.formChoiceNoOptions', vars: { name: f.name }, severity: 'error' });
      }
      const saveAs = typeof d.save_as === 'string' ? d.save_as.trim() : '';
      if (saveAs && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(saveAs)) problems.push({ key: 'flows.validate.badVariableName', vars: { name: saveAs }, severity: 'error' });
      return problems;
    }
  },
  /**
   * Book (7 October 2026): free days, then times, the service's questions, a summary to say yes to, then the
   * booking. `service_name` and `staff_name` are kept for the card only; the engine reads the ids.
   */
  booking: {
    kind: 'booking',
    group: 'actions',
    icon: 'calendarCheck',
    tint: '#0e7490',
    defaults: { name: '', choose_service: true, staff: 'any', days_ahead: BOOKING_DAYS_DEFAULT, confirm: true },
    outputs: () => BOOKING_OUTPUTS,
    summary: (d) => {
      if (bookingChoosesService(d)) return { key: 'flows.builder.summary.bookingChoose' };
      const service = typeof d.service_name === 'string' && d.service_name.trim() ? d.service_name.trim() : '';
      return service ? { key: 'flows.builder.summary.bookingService', vars: { service: str(service, 40) } } : { key: 'flows.builder.summary.bookingSomeService' };
    },
    validate: (d) => {
      const problems: NodeProblem[] = [...bookingDaysProblem(d)];
      if (d.choose_service !== true && !(typeof d.service_id === 'string' && d.service_id.trim())) problems.push({ key: 'flows.validate.bookingNoService', severity: 'error' });
      if (d.ask !== undefined && !(Array.isArray(d.ask) && d.ask.every((k) => typeof k === 'string'))) problems.push({ key: 'flows.validate.bookingAsk', severity: 'error' });
      return problems;
    }
  },
  /** Manage booking: confirm, move or cancel one of the customer's upcoming bookings. No settings beyond the days ahead. */
  manage_booking: {
    kind: 'manage_booking',
    group: 'actions',
    icon: 'calendarEdit',
    tint: '#0369a1',
    defaults: { name: '' },
    outputs: () => MANAGE_BOOKING_OUTPUTS,
    summary: () => ({ key: 'flows.builder.summary.manageBooking' }),
    validate: (d) => bookingDaysProblem(d)
  },
  /**
   * Ticket (7 October 2026): opens a ticket for the team with the run's conversation and contact.
   * `type_name`, `priority_name` and `team_name` are kept for the card only; the engine reads the ids.
   */
  ticket: {
    kind: 'ticket',
    group: 'actions',
    icon: 'ticket',
    tint: '#b45309',
    defaults: { name: '', subject: TICKET_DEFAULT_SUBJECT, notify_client: true },
    outputs: () => TICKET_OUTPUTS,
    summary: (d) => {
      const type = typeof d.type_name === 'string' && d.type_name.trim() ? d.type_name.trim() : '';
      return type ? { key: 'flows.builder.summary.ticketType', vars: { type: str(type, 40) } } : { key: 'flows.builder.summary.ticket' };
    },
    validate: ticketProblems
  },
  /**
   * Change language (3 November 2026): lists the languages the flow's chatbot speaks, each in its own name, saves the
   * pick on the contact and for the channel, and every step after it speaks that language. `question` is optional
   * (the engine asks its own line in the customer's language); `confirm` says so in the new language. It runs on
   * every channel, so it has no channel rule: USSD shows it as a numbered menu.
   */
  language: {
    kind: 'language',
    group: 'actions',
    icon: 'language',
    tint: '#4f46e5',
    defaults: { name: '', confirm: true },
    outputs: () => LANGUAGE_OUTPUTS,
    summary: (d) => {
      const q = typeof d.question === 'string' ? d.question.trim() : '';
      return q ? { text: body(q, 52) } : { key: 'flows.builder.summary.language' };
    }
  },
  /** Ticket status: tells the client where their open requests stand. Nothing to set. */
  ticket_status: {
    kind: 'ticket_status',
    group: 'actions',
    icon: 'ticketSearch',
    tint: '#92400e',
    defaults: { name: '' },
    outputs: () => TICKET_STATUS_OUTPUTS,
    summary: () => ({ key: 'flows.builder.summary.ticketStatus' })
  },
  /**
   * Look up (19 October 2026): reads one of the business's live sources now, with details from the flow, and keeps its
   * data points as variables under the source's name. `source_name` and `points` are kept for the card and the
   * variable list only; the engine reads `source_id`, `values` and `save_as`.
   */
  lookup: {
    kind: 'lookup',
    group: 'actions',
    icon: 'lookup',
    tint: '#0f766e',
    defaults: { name: '' },
    outputs: () => LOOKUP_OUTPUTS,
    summary: (d) => {
      const source = typeof d.source_name === 'string' && d.source_name.trim() ? d.source_name.trim() : '';
      return source ? { key: 'flows.builder.summary.lookup', vars: { source: str(source, 40) } } : { key: 'flows.builder.summary.lookupNone' };
    },
    validate: (d) => (typeof d.source_id === 'string' && d.source_id.trim() ? [] : [{ key: 'flows.validate.lookupNoSource', severity: 'error' }])
  },
  llm: {
    kind: 'llm',
    channelRule: (d) => { const mode = llmMode(d); return mode === 'classify' || mode === 'extract' ? null : mode === 'rewrite' ? 'llm.rewrite' : 'llm.reply'; },
    group: 'actions',
    icon: 'sparkles',
    tint: '#8b5cf6',
    defaults: { name: '', mode: 'reply', kb_scope: 'all', kb_ids: [], send_reply: true, unsure_below: 0.6, system_prompt: '', save_as: 'llm', labels: [], fields: [] },
    outputs: (d) => {
      const mode = llmMode(d);
      if (mode === 'classify') return [...namedItems(d, 'labels').map((l) => ({ id: l.id, label: l.title })), { id: 'other', label: 'Other' }]; // i18n-ignore: port id
      if (mode === 'extract') return FOUND_MISSING;
      // Putting data into words has one way out: there is nothing to be unsure about.
      if (mode === 'rewrite') return [];
      return ANSWERED_UNSURE;
    },
    summary: (d): ReturnType<NodeKindSpec['summary']> => {
      const mode = llmMode(d);
      if (mode === 'classify') {
        const labels = namedItems(d, 'labels');
        return labels.length ? { text: labels.map((l) => l.title).join(' / ') } : { key: 'flows.builder.summary.noLabels' };
      }
      if (mode === 'extract') {
        const fields = namedItems(d, 'fields');
        return fields.length ? { text: fields.map((f) => `{{${f.id}}}`).join(' ') } : { key: 'flows.builder.summary.noFields2' };
      }
      if (mode === 'rewrite') {
        const input = typeof d.input === 'string' ? d.input.trim() : '';
        return input ? { key: 'flows.builder.summary.llmRewrite', vars: { input: str(input, 32) } } : { key: 'flows.builder.summary.llmRewriteNone' };
      }
      const scope = kbScope(d);
      if (scope === 'all') return { key: 'flows.builder.summary.llmAll' };
      if (scope === 'selected') return { key: 'flows.builder.summary.llmSelected', vars: { kb: Array.isArray(d.kb_ids) ? d.kb_ids.length : 0 } };
      return { key: 'flows.builder.summary.llmNone' };
    },
    validate: (d) => {
      const mode = llmMode(d);
      if (mode === 'classify' && namedItems(d, 'labels').length === 0) return [{ key: 'flows.validate.llmNoLabels', severity: 'error' }];
      if (mode === 'extract') {
        const fields = namedItems(d, 'fields');
        if (fields.length === 0) return [{ key: 'flows.validate.llmNoFields', severity: 'error' }];
        const bad = fields.find((f) => !/^[A-Za-z_][A-Za-z0-9_]*$/.test(f.id));
        return bad ? [{ key: 'flows.validate.badVariableName', vars: { name: bad.id }, severity: 'error' }] : [];
      }
      if (mode === 'rewrite' && !(typeof d.input === 'string' && d.input.trim())) return [{ key: 'flows.validate.llmRewriteNoInput', severity: 'error' }];
      return [];
    }
  },
  template: {
    kind: 'template',
    channelRule: () => 'template',
    group: 'actions',
    icon: 'template',
    tint: '#0284c7',
    defaults: { name: '', template_name: '', language: 'en_US', variables: [] },
    outputs: () => [],
    summary: (d) => {
      const name = typeof d.template_name === 'string' ? d.template_name.trim() : '';
      const n = Array.isArray(d.variables) ? d.variables.length : 0;
      return name ? { key: 'flows.builder.summary.template', vars: { name, n } } : { key: 'flows.builder.summary.noTemplate' };
    },
    validate: (d) => (typeof d.template_name === 'string' && d.template_name.trim() ? [] : [{ key: 'flows.validate.templateNone', severity: 'error' }])
  },
  http: {
    kind: 'http',
    channelRule: (d) => (isPlainObject(d.reply) && d.reply.mode === 'ai' ? 'http.ai_reply' : null),
    group: 'actions',
    icon: 'globe',
    tint: '#2563eb',
    defaults: { name: '', method: 'GET', url: '', headers: {}, save_as: 'http', extract: [] },
    outputs: () => SUCCESS_ERROR,
    summary: (d) => {
      const method = typeof d.method === 'string' ? d.method.toUpperCase() : 'GET';
      // On a saved API connection the url is a path there (19 October 2026): the card names the connection.
      const on = typeof d.connection_id === 'string' && d.connection_id && typeof d.connection_name === 'string' ? `${str(d.connection_name, 20)} ` : '';
      return { text: `${method} ${on}${str(d.url, 44 - on.length) || '…'}` };
    },
    validate: (d) => {
      const problems: NodeProblem[] = [];
      const url = typeof d.url === 'string' ? d.url.trim() : '';
      const connected = typeof d.connection_id === 'string' && d.connection_id.trim().length > 0;
      if (!url) problems.push({ key: 'flows.validate.httpNoUrl', severity: 'error' });
      else if (connected) { if (!url.startsWith('/') && !url.startsWith('{{')) problems.push({ key: 'flows.validate.httpConnectionPath', severity: 'error' }); }
      else if (!/^https?:\/\//i.test(url) && !url.startsWith('{{')) problems.push({ key: 'flows.validate.httpBadUrl', severity: 'error' });
      const auth: JsonObject | null = isPlainObject(d.auth) && !connected ? d.auth : null;
      const authType = auth && typeof auth.type === 'string' ? auth.type : 'none';
      // A secret saved on the server (2 October 2026) is not in the definition: `auth.saved` names it.
      const saved = auth && Array.isArray(auth.saved) ? (auth.saved as unknown[]).map(String) : [];
      const missing = (key: string) => !(auth && typeof auth[key] === 'string' && (auth[key] as string).trim()) && !saved.includes(key);
      const need: Record<string, string[]> = { bearer: ['token'], basic: ['username'], api_key: ['key_value'], custom_header: ['header_name', 'header_value'], oauth2: ['token_url', 'client_id'] };
      for (const field of need[authType] ?? []) {
        if (missing(field)) problems.push({ key: 'flows.validate.httpAuthMissing', vars: { type: authType, field }, severity: 'error' });
      }
      // A key, token or password typed into a header (19 October 2026): anyone who opens the flow can read it, and Build
      // with AI is sent it with every message. Under Authentication it is kept sealed once saved.
      if (isPlainObject(d.headers)) {
        for (const [name, value] of Object.entries(d.headers)) {
          const text = typeof value === 'string' ? value.trim() : '';
          if (text.length >= 8 && !text.includes('{{') && !/idempotency/i.test(name) && /key|token|secret|password|authori[sz]ation|bearer/i.test(name))
            problems.push({ key: 'flows.validate.httpSecretInHeader', vars: { header: name }, severity: 'warning' });
        }
      }
      const reply: JsonObject | null = isPlainObject(d.reply) ? d.reply : null;
      if (reply && reply.send !== false) {
        const paths = Array.isArray(reply.paths) ? reply.paths : [];
        const text = typeof reply.text === 'string' ? reply.text.trim() : '';
        const fallback = typeof reply.fallback === 'string' ? reply.fallback.trim() : '';
        const instruction = typeof reply.instruction === 'string' ? reply.instruction.trim() : '';
        // With the AI wording the reply there are no fields to choose; it only helps to say what to tell the customer.
        if (reply.mode === 'ai') { if (!instruction) problems.push({ key: 'flows.validate.httpAiNoInstruction', severity: 'warning' }); }
        else if (paths.length === 0 && !text && !fallback) problems.push({ key: 'flows.validate.httpReplyEmpty', severity: 'warning' });
      }
      return problems;
    }
  },
  contact: {
    kind: 'contact',
    group: 'actions',
    icon: 'user',
    tint: '#f97316',
    /*
     * `confirm_changes` (4 October 2026): a name, email address or language
     * that is already there is not replaced until the customer says yes. The
     * engine reads an absent value as on; a new step says so explicitly.
     */
    defaults: { name: '', fields: {}, add_tags: [], remove_tags: [], confirm_changes: true },
    outputs: () => [],
    summary: (d) => {
      const parts: string[] = [];
      if (typeof d.contact_name === 'string' && d.contact_name.trim()) parts.push(`name=${str(d.contact_name, 16)}`);
      if (typeof d.email === 'string' && d.email.trim()) parts.push('email');
      if (typeof d.locale === 'string' && d.locale.trim()) parts.push(`locale=${str(d.locale, 12)}`);
      const fields = d.fields && typeof d.fields === 'object' ? Object.keys(d.fields as JsonObject) : [];
      if (fields.length) parts.push(fields.join(', '));
      const tags = Array.isArray(d.add_tags) ? d.add_tags.filter(Boolean) : [];
      if (tags.length) parts.push(`+${tags.join(',')}`);
      if (d.opt_out === true) parts.push('opt-out'); // i18n-ignore: a flag name shown as-is
      if (!parts.length) return { key: 'flows.builder.summary.nothingToUpdate' };
      // Asking only ever applies to the three details a customer already has.
      const asks = d.confirm_changes !== false && [d.contact_name ?? d.set_name, d.email, d.locale].some((v) => typeof v === 'string' && v.trim());
      return asks ? { key: 'flows.builder.summary.contactAsks', vars: { parts: str(parts.join(' · '), 44) } } : { text: str(parts.join(' · ')) };
    },
    validate: (d) => {
      const has = (typeof d.contact_name === 'string' && d.contact_name.trim())
        || (typeof d.email === 'string' && d.email.trim())
        || (typeof d.locale === 'string' && d.locale.trim())
        || (d.fields && typeof d.fields === 'object' && Object.keys(d.fields as JsonObject).length > 0)
        || (Array.isArray(d.add_tags) && d.add_tags.length > 0)
        || (Array.isArray(d.remove_tags) && d.remove_tags.length > 0)
        || d.opt_out === true || d.opt_in === true;
      const problems: NodeProblem[] = has ? [] : [{ key: 'flows.validate.contactNothing', severity: 'warning' }];
      // A language menu built by hand (3 November 2026): a custom field called "language" changes nothing the chatbot says.
      const field = languageField(d);
      if (field) problems.push({ key: 'flows.validate.contactLanguageField', vars: { field }, severity: 'warning' });
      return problems;
    }
  },
  handoff: {
    kind: 'handoff',
    channelRule: () => 'handoff',
    group: 'actions',
    icon: 'people',
    tint: '#f59e0b',
    defaults: { name: '', always: true, reason: 'customer_request', message: '' },
    outputs: (d) => [...(d.always === true ? [] : [{ id: 'no', label: 'Kept by bot' }]), { id: 'no_agent', label: 'No agent' }], // i18n-ignore: port ids
    summary: (d) => (d.always === true
      ? { key: 'flows.builder.summary.handoffAlways' }
      : { key: 'flows.builder.summary.handoffThreshold', vars: { value: typeof d.threshold === 'number' ? d.threshold : 0.6 } }),
    validate: (d) => {
      const threshold = Number(d.threshold);
      return Number.isFinite(threshold) && (threshold < 0 || threshold > 1)
        ? [{ key: 'flows.validate.handoffThreshold', vars: { value: String(d.threshold) }, severity: 'error' }]
        : [];
    }
  },
  flow: {
    kind: 'flow',
    group: 'logic',
    icon: 'flow',
    tint: '#0f766e',
    defaults: { name: '', flow_id: '' },
    outputs: () => [],
    summary: (d) => (typeof d.flow_name === 'string' && d.flow_name ? { text: d.flow_name } : { key: 'flows.builder.summary.flowNone' }),
    validate: (d) => (typeof d.flow_id === 'string' && /^[0-9a-f-]{36}$/i.test(d.flow_id) ? [] : [{ key: 'flows.validate.flowNone', severity: 'error' }])
  },
  notify: {
    kind: 'notify',
    group: 'actions',
    icon: 'bell',
    tint: '#dc2626',
    defaults: { name: '', channels: [{ type: 'email', to: '' }], subject: '', message: '', include_details: true, include_transcript: true },
    outputs: () => [],
    summary: (d) => {
      const ways = notifyChannels(d).filter((c) => c.target);
      return ways.length
        ? { text: ways.map((c) => `${c.type}: ${c.target}`).join(' · ') }
        : { key: 'flows.builder.summary.notifyNone' };
    },
    validate: (d) => {
      const ways = notifyChannels(d);
      if (ways.length === 0 || ways.every((c) => !c.target)) return [{ key: 'flows.validate.notifyNone', severity: 'error' }];
      const bad = ways.find((c) => c.target && !validTarget(c));
      return bad ? [{ key: 'flows.validate.notifyBadTarget', vars: { value: bad.target }, severity: 'error' }] : [];
    }
  },
  /** A text to the customer by SMS (4 October 2026). One way on; whether it went is in `{{sms.ok}}`. */
  sms: {
    kind: 'sms',
    group: 'actions',
    icon: 'phone',
    tint: '#0891b2',
    defaults: { name: '', message: '', to: '' },
    outputs: () => [],
    summary: (d) => {
      const message = typeof d.message === 'string' ? d.message.trim() : '';
      if (!message) return { key: 'flows.builder.summary.smsNone' };
      const to = typeof d.to === 'string' ? d.to.trim() : '';
      return to ? { text: `${str(to, 20)} · ${str(message, 40)}` } : { text: str(message) };
    },
    validate: (d) => (typeof d.message === 'string' && d.message.trim() ? [] : [{ key: 'flows.validate.smsNoText', severity: 'error' }])
  },
  /**
   * An email to the customer (4 October 2026). One way on; whether it went is
   * in `{{email.ok}}` and why not in `{{email.detail}}`. Skipped when the
   * customer has no email address, as the engine does.
   */
  email: {
    kind: 'email',
    group: 'actions',
    icon: 'mail',
    tint: '#7c3aed',
    defaults: { name: '', subject: '', message: '', to: '' },
    outputs: () => [],
    summary: (d) => {
      const subject = typeof d.subject === 'string' ? d.subject.trim() : '';
      const message = typeof d.message === 'string' ? d.message.trim() : '';
      if (!subject && !message) return { key: 'flows.builder.summary.emailNone' };
      const to = typeof d.to === 'string' ? d.to.trim() : '';
      const what = subject || str(message, 40);
      return to ? { text: `${str(to, 24)} · ${str(what, 40)}` } : { text: str(what) };
    },
    validate: (d) => {
      const problems: NodeProblem[] = [];
      if (!(typeof d.subject === 'string' && d.subject.trim())) problems.push({ key: 'flows.validate.emailNoSubject', severity: 'error' });
      if (!(typeof d.message === 'string' && d.message.trim())) problems.push({ key: 'flows.validate.emailNoText', severity: 'error' });
      return problems;
    }
  },
  set: {
    kind: 'set',
    group: 'logic',
    icon: 'braces',
    tint: '#64748b',
    defaults: { name: '', assignments: [{ name: '', value: '' }] },
    outputs: () => [],
    summary: (d) => {
      const names = Array.isArray(d.assignments)
        ? d.assignments.map((a) => (a && typeof a === 'object' ? String((a as JsonObject).name ?? '') : '')).filter(Boolean)
        : d.variables && typeof d.variables === 'object' ? Object.keys(d.variables as JsonObject) : [];
      return names.length ? { text: names.map((n) => `{{${n}}}`).join(' ') } : { key: 'flows.builder.summary.nothingToSet' };
    },
    validate: (d) => {
      const problems: NodeProblem[] = [];
      const names = Array.isArray(d.assignments)
        ? d.assignments.map((a) => (a && typeof a === 'object' ? String((a as JsonObject).name ?? '').trim() : ''))
        : [];
      if (names.length === 0 || names.every((n) => !n)) problems.push({ key: 'flows.validate.setNothing', severity: 'warning' });
      for (const n of names) if (n && !/^[A-Za-z_][A-Za-z0-9_.]*$/.test(n)) problems.push({ key: 'flows.validate.badVariableName', vars: { name: n }, severity: 'error' });
      return problems;
    }
  },
  condition: {
    kind: 'condition',
    group: 'logic',
    icon: 'branch',
    tint: '#eab308',
    defaults: { name: '', match: 'all', rules: [{ left: '', op: 'eq', right: '' }] },
    outputs: () => YES_NO,
    summary: (d) => {
      const rules = Array.isArray(d.rules) ? d.rules : [];
      const first = rules[0] && typeof rules[0] === 'object' ? (rules[0] as JsonObject) : null;
      if (!first || !first.left) return { key: 'flows.builder.summary.noRules' };
      const more = rules.length > 1 ? ` +${rules.length - 1}` : '';
      return { text: `${str(first.left, 18)} ${String(first.op ?? 'eq')} ${str(first.right, 18)}${more}` };
    },
    validate: (d) => {
      const rules = Array.isArray(d.rules) ? d.rules : [];
      const usable = rules.filter((r) => r && typeof r === 'object' && String((r as JsonObject).left ?? '').trim());
      return usable.length === 0 ? [{ key: 'flows.validate.conditionNoRules', severity: 'error' }] : [];
    }
  },
  decision: {
    kind: 'decision',
    group: 'logic',
    icon: 'decision',
    tint: '#d97706',
    defaults: { name: '', paths: [{ id: 'path_1', label: '', match: 'all', rules: [{ left: '', op: 'filled', right: '' }] }] },
    outputs: (d) => [
      ...decisionPaths(d).map((p, i) => ({ id: p.id, label: p.label || `Path ${i + 1}` })), // i18n-ignore: a placeholder until the path is named
      { id: DECISION_OTHERWISE, label: 'Otherwise' } // i18n-ignore: port id; label re-mapped by nodes.tsx
    ],
    summary: (d) => {
      const n = decisionPaths(d).length;
      return n ? { key: 'flows.builder.summary.decision', vars: { n } } : { key: 'flows.builder.summary.noPaths' };
    },
    validate: (d) => {
      const problems: NodeProblem[] = [];
      const paths = decisionPaths(d);
      if (paths.length === 0) return [{ key: 'flows.validate.decisionNoPaths', severity: 'error' }];
      paths.forEach((p, i) => {
        const path = p.label || String(i + 1);
        const usable = p.rules.filter((r) => r.left);
        if (usable.length === 0) problems.push({ key: 'flows.validate.decisionPathNoRules', vars: { path }, severity: 'error' });
        for (const r of usable) {
          const kind = opRight(r.op);
          if (!DECISION_OP_LIST.includes(r.op)) problems.push({ key: 'flows.validate.decisionBadOp', vars: { path, op: r.op }, severity: 'error' });
          else if (kind === 'days' && !/^\d+$/.test(r.right.trim())) problems.push({ key: 'flows.validate.decisionBadNumber', vars: { path }, severity: 'error' });
          else if (kind !== 'none' && kind !== 'days' && !r.right.trim()) problems.push({ key: 'flows.validate.decisionRuleNoValue', vars: { path }, severity: 'error' });
        }
        if (!p.label) problems.push({ key: 'flows.validate.decisionPathNoLabel', vars: { path }, severity: 'warning' });
      });
      return problems;
    }
  },
  switch: {
    kind: 'switch',
    group: 'logic',
    icon: 'switch',
    tint: '#a855f7',
    defaults: { name: '', variable: '', cases: [{ value: '' }] },
    outputs: (d) => [...switchCases(d).map((c) => ({ id: c.id, label: c.value })), { id: 'else', label: 'Else' }], // i18n-ignore: port id
    summary: (d) => {
      const v = typeof d.variable === 'string' && d.variable.trim() ? d.variable.trim() : '';
      const n = switchCases(d).length;
      return v ? { key: 'flows.builder.summary.switch', vars: { variable: v, n } } : { key: 'flows.builder.summary.noVariable' };
    },
    validate: (d) => {
      const problems: NodeProblem[] = [];
      if (!(typeof d.variable === 'string' && d.variable.trim())) problems.push({ key: 'flows.validate.switchNoVariable', severity: 'error' });
      if (switchCases(d).length === 0) problems.push({ key: 'flows.validate.switchNoCases', severity: 'error' });
      return problems;
    }
  },
  hours: {
    kind: 'hours',
    group: 'logic',
    icon: 'calendar',
    tint: '#0d9488',
    defaults: { name: '', source: 'business' },
    outputs: () => OPEN_CLOSED,
    summary: (d) => (hoursSource(d) === 'business'
      ? { key: 'flows.builder.summary.hoursBusiness' }
      : { text: `${hoursDays(d)} ${str(d.open, 5) || '08:00'}–${str(d.close, 5) || '17:00'} ${str(d.timezone, 24)}` }),
    validate: (d) => {
      if (hoursSource(d) === 'business') return [];
      const ok = (v: unknown) => typeof v === 'string' && /^\d{1,2}:\d{2}$/.test(v);
      return ok(d.open ?? '08:00') && ok(d.close ?? '17:00') ? [] : [{ key: 'flows.validate.hoursBadTime', severity: 'error' }];
    }
  },
  split: {
    kind: 'split',
    group: 'logic',
    icon: 'split',
    tint: '#f43f5e',
    defaults: { name: '', branches: [{ id: 'a', title: 'A', weight: 50 }, { id: 'b', title: 'B', weight: 50 }] },
    outputs: (d) => splitBranches(d).map((b) => ({ id: b.id, label: b.title })),
    summary: (d) => {
      const branches = splitBranches(d);
      const total = branches.reduce((a, b) => a + b.weight, 0) || 1;
      return { text: branches.map((b) => `${b.title} ${Math.round((b.weight / total) * 100)}%`).join(' · ') };
    }
  },
  delay: {
    kind: 'delay',
    channelRule: () => 'delay',
    group: 'logic',
    icon: 'clock',
    tint: '#ec4899',
    defaults: { name: '', minutes: 5 },
    outputs: () => [],
    summary: (d) => {
      const total = (Number(d.seconds) || 0) + (Number(d.minutes) || 0) * 60 + (Number(d.hours) || 0) * 3600 + (Number(d.days) || 0) * 86400;
      if (total <= 0) return { key: 'flows.builder.summary.noDelay' };
      if (total < 60) return { key: 'flows.builder.summary.delaySeconds', vars: { n: total } };
      if (total < 3600) return { key: 'flows.builder.summary.delayMinutes', vars: { n: Math.round(total / 60) } };
      if (total < 86400) return { key: 'flows.builder.summary.delayHours', vars: { n: Math.round(total / 360) / 10 } };
      return { key: 'flows.builder.summary.delayDays', vars: { n: Math.round(total / 8640) / 10 } };
    },
    validate: (d) => {
      const total = (Number(d.seconds) || 0) + (Number(d.minutes) || 0) * 60 + (Number(d.hours) || 0) * 3600 + (Number(d.days) || 0) * 86400;
      if (total <= 0) return [{ key: 'flows.validate.delayZero', severity: 'error' }];
      if (total > 7 * 86400) return [{ key: 'flows.validate.delayTooLong', severity: 'error' }];
      return [];
    }
  },
  end: {
    kind: 'end',
    group: 'logic',
    icon: 'stop',
    tint: '#ef4444',
    defaults: { name: '', message: '' },
    outputs: () => [],
    summary: (d) => (typeof d.message === 'string' && d.message.trim() ? { text: body(d.message) } : { key: 'flows.builder.summary.endSilent' })
  }
};

/** Mon–Fri, Mon–Sun, or the listed day names, for the business-hours summary. */
function hoursDays(d: JsonObject): string {
  const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // i18n-ignore: ISO day abbreviations in a compact summary
  const days = (Array.isArray(d.days) ? d.days : [1, 2, 3, 4, 5]).map((x) => Number(x)).filter((n) => n >= 1 && n <= 7).sort((a, b) => a - b);
  if (days.length === 0) return '';
  const contiguous = days.every((n, i) => i === 0 || n === days[i - 1] + 1);
  return contiguous && days.length > 1 ? `${names[days[0] - 1]}–${names[days[days.length - 1] - 1]}` : days.map((n) => names[n - 1]).join(',');
}

/** The palette, in the order the reference lays it out. */
export const PALETTE_ORDER: string[] = ['send', 'template', 'sms', 'email', 'ask', 'form', 'language', 'booking', 'manage_booking', 'ticket', 'ticket_status', 'llm', 'http', 'lookup', 'contact', 'notify', 'handoff', 'flow', 'condition', 'decision', 'switch', 'hours', 'split', 'set', 'delay', 'end'];

/** Output ports for a canvas node: none for a single way out. */
export function outputsFor(node: Pick<FlowNodeData, 'type' | 'data'>): FlowNodeOutput[] {
  const spec = NODE_KINDS[node.type];
  if (!spec) return [];
  return spec.outputs((node.data ?? {}) as JsonObject);
}

/** Whether the node can lead anywhere at all. */
export function isTerminal(type: string): boolean {
  return type === 'end';
}

// ── What a flow is for (trigger.intent, 1 October 2026) ─────────────────────

export type IntentMode = 'jump' | 'offer' | 'off';

/** A reply button holds 20 characters on WhatsApp. */
export const INTENT_LABEL_MAX = 20;

export interface TriggerIntent {
  description: string;
  examples: string[];
  mode: IntentMode;
  buttonLabel: string;
}

/** The trigger's `intent` as the form reads it: every field present, the mode defaulting to `jump`. */
export function triggerIntent(d: Record<string, unknown>): TriggerIntent {
  const raw = d.intent && typeof d.intent === 'object' && !Array.isArray(d.intent) ? (d.intent as Record<string, unknown>) : {};
  const examples = Array.isArray(raw.examples)
    ? raw.examples.map(String)
    : typeof raw.examples === 'string' ? raw.examples.split(/\r?\n/) : [];
  const mode = raw.mode === 'offer' || raw.mode === 'off' ? raw.mode : 'jump';
  return {
    description: typeof raw.description === 'string' ? raw.description : '',
    examples,
    mode,
    buttonLabel: typeof raw.button_label === 'string' ? raw.button_label : ''
  };
}

/** Trigger types whose flow a customer can ask for: a keyword flow, or one started from the business's own system. */
export function intentAllowed(d: Record<string, unknown>): boolean {
  return d.type === undefined || d.type === 'keyword' || d.type === 'webhook';
}

/** Will routing use this trigger's intent: switched on, with something to compare a message with. */
export function intentEnabled(d: Record<string, unknown>): boolean {
  if (!intentAllowed(d)) return false;
  const intent = triggerIntent(d);
  return intent.mode !== 'off' && (intent.description.trim().length > 0 || intent.examples.some((e) => e.trim().length > 0));
}

/** How a flow that says what it is for is brought to a customer who asks for it; null when it does not say. */
export function flowAskedFor(definition: unknown): 'jump' | 'offer' | null {
  const def = definition && typeof definition === 'object' ? (definition as { trigger?: unknown }) : null;
  const trigger = def?.trigger && typeof def.trigger === 'object' && !Array.isArray(def.trigger) ? (def.trigger as Record<string, unknown>) : null;
  if (!trigger || !intentEnabled(trigger)) return null;
  return triggerIntent(trigger).mode === 'offer' ? 'offer' : 'jump';
}

/**
 * What is wrong with a step on the channels a flow runs on: one warning per
 * channel that cannot run it, worded per rule ("This step does not work on
 * USSD: …"). Empty for a step that runs everywhere.
 */
export function channelProblems(type: string, data: JsonObject, channels: readonly string[]): NodeProblem[] {
  const rule = NODE_KINDS[type]?.channelRule?.(data) ?? null;
  if (!rule) return [];
  const out: NodeProblem[] = [];
  for (const channel of new Set(channels.map((c) => c.toLowerCase()))) {
    if (UNAVAILABLE_ON[channel]?.includes(rule)) out.push({ key: `flows.validate.${channel}.${rule}`, severity: 'warning' });
  }
  return out;
}
